import React, { useRef, useMemo, useState, useEffect } from 'react';
import { Canvas, useFrame, useLoader } from '@react-three/fiber';
import { OrbitControls, Sphere, Html } from '@react-three/drei';
import * as THREE from 'three';

// ============================================================================
// TEXTURE URLs (NASA Blue Marble & Natural Earth)
// ============================================================================
const EARTH_TEXTURES = {
  // Earth day map (land and ocean)
  dayMap: 'https://unpkg.com/three-globe@2.31.0/example/img/earth-blue-marble.jpg',
  // Bump map for terrain elevation
  bumpMap: 'https://unpkg.com/three-globe@2.31.0/example/img/earth-topology.png',
  // Specular map (water reflection)
  specularMap: 'https://unpkg.com/three-globe@2.31.0/example/img/earth-water.png',
  // Clouds (from Three.js examples)
  cloudsMap: 'https://raw.githubusercontent.com/mrdoob/three.js/dev/examples/textures/planets/earth_clouds_1024.png',
};

// GeoJSON URL for country borders
const COUNTRIES_GEOJSON_URL = 'https://raw.githubusercontent.com/datasets/geo-countries/master/data/countries.geojson';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// Convert lat/lng to 3D coordinates on sphere
const latLngToVector3 = (lat, lng, radius) => {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lng + 180) * (Math.PI / 180);

  const x = -(radius * Math.sin(phi) * Math.cos(theta));
  const z = radius * Math.sin(phi) * Math.sin(theta);
  const y = radius * Math.cos(phi);

  return new THREE.Vector3(x, y, z);
};

// Convert GeoJSON coordinates to 3D points
const geoJsonTo3DPoints = (coordinates, radius) => {
  const points = [];
  coordinates.forEach(([lng, lat]) => {
    points.push(latLngToVector3(lat, lng, radius));
  });
  return points;
};

// ============================================================================
// COUNTRY BORDERS COMPONENT
// ============================================================================
const CountryBorders = ({ radius }) => {
  const [borders, setBorders] = useState([]);
  const groupRef = useRef();

  useEffect(() => {
    // Fetch GeoJSON data
    fetch(COUNTRIES_GEOJSON_URL)
      .then(res => res.json())
      .then(data => {
        const borderLines = [];

        data.features.forEach((feature, featureIndex) => {
          const { geometry } = feature;

          const processCoordinates = (coords, type) => {
            if (type === 'Polygon') {
              // Polygon: array of rings, first ring is outer boundary
              coords.forEach((ring, ringIndex) => {
                if (ring.length > 1) {
                  const points = geoJsonTo3DPoints(ring, radius + 0.002);
                  borderLines.push({
                    key: `${featureIndex}-${ringIndex}`,
                    points
                  });
                }
              });
            } else if (type === 'MultiPolygon') {
              // MultiPolygon: array of polygons
              coords.forEach((polygon, polyIndex) => {
                polygon.forEach((ring, ringIndex) => {
                  if (ring.length > 1) {
                    const points = geoJsonTo3DPoints(ring, radius + 0.002);
                    borderLines.push({
                      key: `${featureIndex}-${polyIndex}-${ringIndex}`,
                      points
                    });
                  }
                });
              });
            }
          };

          if (geometry.type === 'Polygon') {
            processCoordinates(geometry.coordinates, 'Polygon');
          } else if (geometry.type === 'MultiPolygon') {
            processCoordinates(geometry.coordinates, 'MultiPolygon');
          }
        });

        setBorders(borderLines);
      })
      .catch(err => console.error('Failed to load country borders:', err));
  }, [radius]);

  return (
    <group ref={groupRef}>
      {borders.map(({ key, points }) => (
        <line key={key}>
          <bufferGeometry>
            <bufferAttribute
              attach="attributes-position"
              count={points.length}
              array={new Float32Array(points.flatMap(p => [p.x, p.y, p.z]))}
              itemSize={3}
            />
          </bufferGeometry>
          <lineBasicMaterial
            color="#ffcc00"
            transparent
            opacity={0.6}
            linewidth={1}
          />
        </line>
      ))}
    </group>
  );
};

// ============================================================================
// COUNTRY MARKER COMPONENT
// ============================================================================
const CountryMarker = ({ country, radius, onClick, isSelected }) => {
  const [hovered, setHovered] = useState(false);
  const position = useMemo(() => {
    if (!country.latlng || country.latlng.length < 2) return null;
    return latLngToVector3(country.latlng[0], country.latlng[1], radius + 0.03);
  }, [country.latlng, radius]);

  if (!position) return null;

  const markerScale = isSelected ? 0.05 : hovered ? 0.04 : 0.025;

  return (
    <mesh
      position={position}
      onClick={(e) => {
        e.stopPropagation();
        onClick(country);
      }}
      onPointerOver={(e) => {
        e.stopPropagation();
        setHovered(true);
        document.body.style.cursor = 'pointer';
      }}
      onPointerOut={(e) => {
        setHovered(false);
        document.body.style.cursor = 'auto';
      }}
    >
      <sphereGeometry args={[markerScale, 16, 16]} />
      <meshBasicMaterial
        color={isSelected ? '#ff3333' : hovered ? '#00ffff' : '#ff6600'}
        transparent
        opacity={isSelected ? 1 : hovered ? 0.95 : 0.8}
      />
      {hovered && (
        <Html distanceFactor={3}>
          <div style={{
            background: 'rgba(0, 0, 0, 0.85)',
            color: '#fff',
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: '500',
            whiteSpace: 'nowrap',
            transform: 'translateX(-50%)',
            border: '1px solid rgba(255,255,255,0.2)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          }}>
            {country.name}
          </div>
        </Html>
      )}
    </mesh>
  );
};

// ============================================================================
// TEXTURED EARTH COMPONENT
// ============================================================================
const TexturedEarth = ({ countries, onCountryClick, selectedCountry, radius = 2 }) => {
  const earthRef = useRef();
  const cloudsRef = useRef();

  // Load all textures
  const [dayTexture, bumpTexture, specularTexture, cloudsTexture] = useLoader(
    THREE.TextureLoader,
    [
      EARTH_TEXTURES.dayMap,
      EARTH_TEXTURES.bumpMap,
      EARTH_TEXTURES.specularMap,
      EARTH_TEXTURES.cloudsMap,
    ]
  );

  // Slow rotation
  useFrame(({ clock }) => {
    if (earthRef.current && !selectedCountry) {
      earthRef.current.rotation.y += 0.0005;
    }
    if (cloudsRef.current) {
      cloudsRef.current.rotation.y += 0.0007;
    }
  });

  return (
    <group ref={earthRef}>
      {/* Main Earth sphere with textures */}
      <Sphere args={[radius, 128, 128]}>
        <meshPhongMaterial
          map={dayTexture}
          bumpMap={bumpTexture}
          bumpScale={0.05}
          specularMap={specularTexture}
          specular={new THREE.Color('#333333')}
          shininess={15}
        />
      </Sphere>

      {/* Clouds layer */}
      <Sphere ref={cloudsRef} args={[radius * 1.01, 64, 64]}>
        <meshPhongMaterial
          map={cloudsTexture}
          transparent
          opacity={0.35}
          depthWrite={false}
        />
      </Sphere>

      {/* Atmosphere glow */}
      <Sphere args={[radius * 1.025, 64, 64]}>
        <meshBasicMaterial
          color="#4da6ff"
          transparent
          opacity={0.08}
          side={THREE.BackSide}
        />
      </Sphere>

      {/* Country borders */}
      <CountryBorders radius={radius} />

      {/* Country markers */}
      {countries.map((country) => (
        <CountryMarker
          key={country.cca3 || country.name}
          country={country}
          radius={radius}
          onClick={onCountryClick}
          isSelected={selectedCountry?.cca3 === country.cca3}
        />
      ))}
    </group>
  );
};

// ============================================================================
// STARS BACKGROUND
// ============================================================================
const Stars = () => {
  const starsRef = useRef();
  const starCount = 3000;

  const [positions, colors] = useMemo(() => {
    const pos = new Float32Array(starCount * 3);
    const col = new Float32Array(starCount * 3);

    for (let i = 0; i < starCount; i++) {
      const radius = 50 + Math.random() * 100;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;

      pos[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      pos[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      pos[i * 3 + 2] = radius * Math.cos(phi);

      // Vary star colors slightly (white to blue-ish)
      const brightness = 0.7 + Math.random() * 0.3;
      col[i * 3] = brightness;
      col[i * 3 + 1] = brightness;
      col[i * 3 + 2] = brightness + Math.random() * 0.1;
    }
    return [pos, col];
  }, []);

  useFrame(() => {
    if (starsRef.current) {
      starsRef.current.rotation.y += 0.00005;
    }
  });

  return (
    <points ref={starsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={starCount}
          array={positions}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          count={starCount}
          array={colors}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.15}
        sizeAttenuation
        vertexColors
        transparent
        opacity={0.9}
      />
    </points>
  );
};

// ============================================================================
// LOADING COMPONENT
// ============================================================================
const LoadingScreen = () => (
  <div style={{
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0a0a1a 0%, #1a1a2e 100%)',
    color: '#fff',
    zIndex: 100,
  }}>
    <div style={{
      width: '60px',
      height: '60px',
      border: '3px solid rgba(255,255,255,0.1)',
      borderTop: '3px solid #4da6ff',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
    }} />
    <style>{`
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `}</style>
    <p style={{ marginTop: '20px', fontSize: '14px', color: '#888' }}>
      Loading Earth textures...
    </p>
  </div>
);

// ============================================================================
// MAIN GLOBE COMPONENT
// ============================================================================
const Globe = ({ countries, onCountryClick, selectedCountry }) => {
  return (
    <div style={{ width: '100%', height: '100vh', position: 'fixed', top: 0, left: 0 }}>
      <React.Suspense fallback={<LoadingScreen />}>
        <Canvas
          camera={{ position: [0, 0, 5], fov: 45 }}
          style={{ background: 'linear-gradient(135deg, #0a0a1a 0%, #0d0d1a 50%, #1a1a2e 100%)' }}
          gl={{ antialias: true, alpha: false }}
        >
          {/* Lighting */}
          <ambientLight intensity={0.2} />
          <directionalLight
            position={[5, 3, 5]}
            intensity={1.5}
            color="#ffffff"
          />
          <directionalLight
            position={[-5, -3, -5]}
            intensity={0.3}
            color="#4da6ff"
          />
          <pointLight position={[10, 10, 10]} intensity={0.5} color="#ffffff" />

          {/* Stars background */}
          <Stars />

          {/* Textured Earth with borders and markers */}
          <TexturedEarth
            countries={countries}
            onCountryClick={onCountryClick}
            selectedCountry={selectedCountry}
          />

          {/* Camera controls */}
          <OrbitControls
            enablePan={false}
            enableZoom={true}
            minDistance={2.5}
            maxDistance={12}
            autoRotate={!selectedCountry}
            autoRotateSpeed={0.3}
            enableDamping
            dampingFactor={0.05}
            rotateSpeed={0.5}
            zoomSpeed={0.8}
          />
        </Canvas>
      </React.Suspense>
    </div>
  );
};

export default Globe;
